import { type CancelToken } from 'axios';
import {
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import {
  isApiError, isCancelError,
} from '~/_shared/utils/api/apiError.helpers';
import { debounce } from '~/_shared/utils/debounce';
import { useTranslation } from '~/_shared/utils/hooks';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import type { ConstraintsValidationDataMap } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.types';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { type MetricValue } from '../../createBoundaryGroupFromAIModal.types';
import { getConstraintValidationErrorMessages } from './constraints.helpers';
import {
  validateWMSTerritoryConstraints,
  type ValidateWMSTerritoryConstraintsItem,
} from './constraints.repository';

type WMSConstraintsValidationParams = {
  constraintsEnabled: boolean;
  dataMetricsValues: readonly MetricValue[];
  demographicsValues: readonly MetricValue[];
  zipCodesSubset?: readonly string[];
  statesSubset?: readonly string[];
};

export type WMSConstraintValidationState = {
  areConstraintsValid: boolean;
  generalError: string;
  isLoading: boolean;
  validationData: ConstraintsValidationDataMap;
};

const executeDebounced = debounce((fn: () => void) => fn(), 750);

export const useWMSConstraintsValidation = ({
  constraintsEnabled,
  demographicsValues,
  dataMetricsValues,
  zipCodesSubset,
  statesSubset,
}: WMSConstraintsValidationParams): WMSConstraintValidationState => {
  const [validationData, setValidationData] = useState<ConstraintsValidationDataMap>(new Map());
  const [generalError, setGeneralError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const spreadsheetId = usePrimarySpreadsheetId();
  const mapId = useMapIdSelector();

  const constraints = useMemo(() => [
    ...dataMetricsValues.filter(dataMetricsValue => dataMetricsValue.isConstraint && dataMetricsValue.checked),
    ...demographicsValues.filter(demographicsValue => demographicsValue.isConstraint && demographicsValue.checked),
  ], [dataMetricsValues, demographicsValues]);

  const validateConstraints = useCallback(async (cancelToken: CancelToken) => {
    if (!clientId || mapId === null || spreadsheetId === null) {
      return;
    }

    const validationItems: ValidateWMSTerritoryConstraintsItem[] = constraints.map(constraint => ({
      metric: constraint.id,
      min: constraint.constraintLowerBound ?? 1,
      max: constraint.constraintUpperBound ?? Number.MAX_SAFE_INTEGER,
    }));

    if (validationItems.length === 0) {
      return;
    }

    setIsLoading(true);

    try {
      const validationResult = await validateWMSTerritoryConstraints(clientId, {
        map_id: mapId,
        constraints: validationItems,
        spreadsheet_id: spreadsheetId,
        dataset_states: statesSubset,
        dataset_zip_codes: zipCodesSubset,
      }, cancelToken);

      setGeneralError('');

      if (validationResult && validationResult.results.length) {
        const newValidationData: ConstraintsValidationDataMap = new Map();

        for (const validationItem of validationResult.results) {
          const errorMessages = getConstraintValidationErrorMessages(validationItem, t);

          newValidationData.set(validationItem.metric, {
            errorMessages,
            highestMin: validationItem.limits.highestMin,
            lowestMax: validationItem.limits.lowestMax,
            max: validationItem.limits.max,
            min: validationItem.limits.min,
          });
        }

        setValidationData(newValidationData);
      }
    }
    catch (err) {
      if (!isApiError(err) || !isCancelError(err)) {
        setValidationData(new Map());
        setGeneralError(t('Something went wrong, please try again.'));
      }
    }
    finally {
      setIsLoading(false);
    }
  }, [clientId, mapId, spreadsheetId, statesSubset, zipCodesSubset, constraints, t]);

  const constraintsCountChangedRef = useRef<boolean>(false);
  const previousConstraintsCount = usePrevious(constraints.length);
  constraintsCountChangedRef.current = previousConstraintsCount !== constraints.length;

  useEffect(() => {
    if (!constraintsEnabled) {
      return;
    }

    const cancelToken = createCancelToken();
    const validateNow = constraintsCountChangedRef.current;

    const validateTask = executeDebounced(() => validateConstraints(cancelToken.token));
    if (validateNow) {
      validateTask.now();
    }

    return () => {
      cancelToken.cancel();
    };
  }, [constraintsEnabled, validateConstraints]);

  return {
    areConstraintsValid: !isLoading && (constraintsEnabled
      ? Array.from(validationData.values()).every(validation => validation.errorMessages.length === 0)
      : true),
    generalError,
    isLoading,
    validationData,
  };
};
